import { getContext, setContext } from "svelte";
import { get, writable, type Readable, type Writable, derived } from "svelte/store";
import type { Folder } from "../newsletters/types";
import type { Newsletter } from "$gql/generated";
import { getClassicWebUrl } from "../currentUser";
import { ServerError, getJson, postJson } from "../http";
import type { MaybePromise } from "@sveltejs/kit";

const CONTEXT_KEY = {};
const PROGRESS_REFRESH_INTERVAL = 2000;

type FolderTree = Folder & { selected: boolean; isOpen: boolean; newsletters: Newsletter[] };
export type CopyNewslettersRequestStatus = "pending" | "processing" | "completed" | "failed" | "seen" | "sent_by_email";

export type CopyRequestProgress = {
	id: string;
	status: CopyNewslettersRequestStatus;
	totalNewsletters: number;
	totalFolders: number;
	copiedNewsletters: number;
	errorCode: string | undefined;
	completedAt?: Date;
	failedAt?: Date;
};

type StoreProps = {
	isLoading: boolean;
	selectAll: boolean;
	folders: FolderTree[];
	myNewsletters: Newsletter[];
	selectedNewslettersInFolder: Record<string, string[]>;
	excludeSelectedNewslettersInFolder: Record<string, string[]>;
	excludedFolderIds: string[];
	errorsDuringCopy: boolean;
	copyRequestId: string | undefined;
	copyRequestProgress: CopyRequestProgress | undefined;
	progressPopupOpen: boolean;
};

type CopyFromSmoreStore = Writable<StoreProps> & {
	setSelectAll(isSelectAll: boolean): void;
	getIsPartiallySelectAll(): Readable<boolean>;
	getData(): Promise<void>;
	changeFolderSelection(folderId: string, selected: boolean): void;
	isFolderSelected(folderId: string): boolean;
	isFolderPartiallySelected(folderId: string): boolean;
	collapseExpandFolder(folderId: string): Promise<void>;
	getFolderIdsDuringFetch(): Readable<string[]>;
	changeNewsletterSelection(newsletterId: string, folderId: string, selected: boolean): void;
	isNewsletterSelected(folderId: string, newsletterId: string): boolean;
	startCopyNewsletters(): Promise<string>;
	resetStore(): void;
	updateCopyNewslettersProgress(): Promise<void>;
	getCopyNewslettersProgress(): Readable<CopyRequestProgress | undefined>;
	checkForExistingCopyRequest(): Promise<void>;
	markOperationAsSeen(id: string): Promise<void>;
};

export function createCopyFromSmoreStore() {
	let store = getContext(CONTEXT_KEY) as CopyFromSmoreStore;
	if (!store) {
		store = createStore();
		setContext(CONTEXT_KEY, store);
	}
	return store;
}

export function getCopyFromSmoreStore(): CopyFromSmoreStore {
	const store = getContext(CONTEXT_KEY) as CopyFromSmoreStore;

	if (!store) {
		throw new Error("You need to call createCopyFromSmoreStore first");
	}

	return store;
}

function createStore(): CopyFromSmoreStore {
	const fetchingFolderNewsletters: string[] = [];
	const classicWebUrl = getClassicWebUrl();
	let openCopyRequestId: string | undefined = undefined;
	let checkProgressInterval: NodeJS.Timeout | undefined = undefined;
	let copyRequestProgressPromise: MaybePromise<any> = undefined;

	const store = writable<StoreProps>({
		isLoading: false,
		selectAll: false,
		folders: [],
		myNewsletters: [],
		selectedNewslettersInFolder: {},
		excludeSelectedNewslettersInFolder: {},
		excludedFolderIds: [],
		errorsDuringCopy: false,
		copyRequestId: undefined,
		copyRequestProgress: undefined,
		progressPopupOpen: false
	});

	async function updateCopyNewslettersProgressInternal() {
		const { copyRequestId } = get(store);
		let copyRequestProgress: CopyRequestProgress | undefined = undefined;

		// In case there's already a call waiting for the progress, do not initiate a new one...
		if (copyRequestId && copyRequestId !== "done" && !copyRequestProgressPromise) {
			copyRequestProgressPromise = getJson<CopyRequestProgress | {}>(
				`${classicWebUrl}/app/connect_copy/copy_newsletters_progress/${copyRequestId}`
			);
			try {
				const copyRequestProgressResult = await copyRequestProgressPromise;
				if ("status" in copyRequestProgressResult) {
					copyRequestProgress = copyRequestProgressResult;
					if (copyRequestProgress?.status === "failed") {
						updateStoreWithErrorsDuringCopy();
					}
				}
			} catch (e) {
				updateStoreWithErrorsDuringCopy();
			} finally {
				copyRequestProgressPromise = undefined;
			}
		}

		updateStoreWithCopyRequestProgress(copyRequestProgress);
	}

	function updateStoreWithErrorsDuringCopy() {
		store.update((values) => {
			return {
				...values,
				errorsDuringCopy: true,
				copyRequestId: undefined,
				copyRequestProgress: undefined
			};
		});
	}

	function updateStoreWithCopyRequestProgress(copyRequestProgress: CopyRequestProgress | undefined) {
		store.update((values) => {
			let { copyRequestId } = values;
			if (!copyRequestProgress || copyRequestProgress?.status === "seen") {
				copyRequestId = undefined;
			}
			openCopyRequestId = copyRequestId;
			return {
				...values,
				copyRequestId,
				copyRequestProgress
			};
		});
		refreshCheckProgress();
	}

	function refreshCheckProgress() {
		const { errorsDuringCopy } = get(store);
		if (!errorsDuringCopy && openCopyRequestId && !checkProgressInterval && openCopyRequestId !== "done") {
			checkProgressInterval = setInterval(updateCopyNewslettersProgressInternal, PROGRESS_REFRESH_INTERVAL);
		}

		if (errorsDuringCopy || (!openCopyRequestId && checkProgressInterval)) {
			clearInterval(checkProgressInterval);
			checkProgressInterval = undefined;
		}
	}

	return {
		subscribe: store.subscribe,
		set: store.set,
		update: store.update,
		setSelectAll(isSelectAll) {
			store.update((values) => {
				values.folders.forEach((folder) => (folder.selected = false));
				return {
					...values,
					selectAll: isSelectAll,
					selectedNewslettersInFolder: {},
					excludeSelectedNewslettersInFolder: {},
					excludedFolderIds: []
				};
			});
		},
		getIsPartiallySelectAll() {
			return derived(store, (values: StoreProps) => {
				const { selectAll, excludeSelectedNewslettersInFolder, excludedFolderIds, selectedNewslettersInFolder, folders } = values;
				if (selectAll) {
					return Object.keys(excludeSelectedNewslettersInFolder).length > 0 || Object.keys(excludedFolderIds).length > 0;
				}
				return (
					Object.values(selectedNewslettersInFolder).findIndex((ids) => ids.length > 0) >= 0 || folders.findIndex((f) => f.selected) >= 0
				);
			});
		},
		async getData() {
			const { isLoading } = get(store);
			if (isLoading) {
				return;
			}
			openCopyRequestId = undefined;
			store.update((values: StoreProps) => {
				return {
					...values,
					isLoading: true,
					selectAll: false,
					folders: [],
					myNewsletters: [],
					selectedNewslettersInFolder: {},
					excludeSelectedNewslettersInFolder: {},
					excludedFolderIds: [],
					// errorsDuringCopy: false,
					copyRequestId: undefined,
					copyRequestProgress: undefined
				};
			});
			const [foldersResult, newslettersResult] = await Promise.all([
				getJson<Folder[]>(`${classicWebUrl}/app/connect_copy/importing_folders`),
				getJson<Newsletter[]>(`${classicWebUrl}/app/connect_copy/importing_newsletters_by_folder/my`)
			]);
			store.update((values: StoreProps) => {
				const folders = foldersResult.map((f) => ({ ...f, selected: false, isOpen: false, newsletters: [] }));
				return {
					...values,
					isLoading: false,
					folders: folders,
					myNewsletters: newslettersResult
				};
			});
		},
		changeFolderSelection(folderId, selected) {
			store.update((values: StoreProps) => {
				const folder = values.folders.find((f) => f.id === folderId);
				const selectedNewslettersInFolder = values.selectedNewslettersInFolder;
				const excludeSelectedNewslettersInFolder = values.excludeSelectedNewslettersInFolder;
				const excludedFolderIds = values.excludedFolderIds;
				if (folder) {
					folder.selected = selected;
					if (!selected) {
						selectedNewslettersInFolder[folderId] = [];
						excludeSelectedNewslettersInFolder[folderId] = [];
						if (values.selectAll) {
							if (!excludedFolderIds.includes(folderId)) {
								excludedFolderIds.push(folderId);
							}
						}
					} else {
						if (values.selectAll) {
							const index = excludedFolderIds.indexOf(folderId);
							if (index >= 0) {
								excludedFolderIds.splice(index, 1);
							}
						}
					}
				}
				return {
					...values,
					selectedNewslettersInFolder,
					excludeSelectedNewslettersInFolder,
					excludedFolderIds
				};
			});
		},
		isFolderSelected(folderId) {
			const { folders, selectAll, excludedFolderIds } = get(store);
			if (selectAll) {
				return !excludedFolderIds.includes(folderId);
			}
			const folder = folders.find((f) => f.id === folderId);
			if (folder) {
				return folder.selected;
			}
			return false;
		},
		isFolderPartiallySelected(folderId) {
			const { folders, selectAll, excludeSelectedNewslettersInFolder, selectedNewslettersInFolder } = get(store);
			if (this.isFolderSelected(folderId)) {
				const folder = folders.find((f) => f.id === folderId);

				if (folder) {
					if (selectAll || folder.selected) {
						const newsletters = excludeSelectedNewslettersInFolder[folder.id] || [];
						return newsletters.length > 0;
					}
				}
				return false;
			}
			return selectedNewslettersInFolder[folderId]?.length > 0;
		},
		async collapseExpandFolder(folderId) {
			const { folders } = get(store);
			const folder = folders.find((f) => f.id === folderId);
			if (folder) {
				folder.isOpen = !folder.isOpen;
				if (folder.isOpen && folder.newsletters.length === 0) {
					const timeout = setTimeout(() => fetchingFolderNewsletters.push(folderId), 300);
					try {
						const folderNewsletters = await getJson<Newsletter[]>(
							`${classicWebUrl}/app/connect_copy/importing_newsletters_by_folder/${folderId}`
						);
						folder.newsletters = folderNewsletters;
						folder.isOpen = true;
					} catch (e) {
						const problematicFolderIndex = folders.findIndex((f) => f.id === folderId);
						folders.splice(problematicFolderIndex, 1);
					}
					clearTimeout(timeout);
					const index = fetchingFolderNewsletters.indexOf(folderId);
					fetchingFolderNewsletters.splice(index, 1);

					store.update((values) => {
						return {
							...values,
							folders
						};
					});
				}
			}
		},
		getFolderIdsDuringFetch() {
			return derived(store, (values: StoreProps) => {
				return fetchingFolderNewsletters;
			});
		},
		changeNewsletterSelection(newsletterId, folderId, selected) {
			store.update((values: StoreProps) => {
				const folder = values.folders.find((f) => f.id === folderId);
				if (!values.selectAll && !folder?.selected) {
					const newsletterIds = values.selectedNewslettersInFolder[folderId] || [];
					const newsletterIdIndex = newsletterIds.indexOf(newsletterId);

					if (newsletterIdIndex >= 0 && !selected) {
						newsletterIds.splice(newsletterIdIndex, 1);
					} else if (newsletterIdIndex < 0 && selected) {
						newsletterIds.push(newsletterId);
					}

					if (newsletterIds.length > 0) {
						values.selectedNewslettersInFolder[folderId] = newsletterIds;
					} else {
						delete values.selectedNewslettersInFolder[folderId];
					}
				} else {
					const newsletterIds = values.excludeSelectedNewslettersInFolder[folderId] || [];
					const newsletterIdIndex = newsletterIds.indexOf(newsletterId);

					if (newsletterIdIndex >= 0 && selected) {
						newsletterIds.splice(newsletterIdIndex, 1);
					} else if (newsletterIdIndex < 0 && !selected) {
						newsletterIds.push(newsletterId);
					}

					if (newsletterIds.length > 0) {
						if (folder?.count === newsletterIds.length) {
							folder.selected = false;
							if (!values.excludedFolderIds.includes(folder.id)) {
								values.excludedFolderIds.push(folder.id);
							}
							delete values.excludeSelectedNewslettersInFolder[folderId];
						} else {
							values.excludeSelectedNewslettersInFolder[folderId] = newsletterIds;
						}
					} else {
						delete values.excludeSelectedNewslettersInFolder[folderId];
					}
				}
				return {
					...values
				};
			});
		},
		isNewsletterSelected(folderId, newsletterId) {
			const data = get(store);
			if (data.selectAll) {
				const isNewsletterIdInExcludedNewslettersOfFolder = (data.excludeSelectedNewslettersInFolder[folderId] || []).includes(
					newsletterId
				);
				return !isNewsletterIdInExcludedNewslettersOfFolder;
			}

			let folder = data.folders.find((f) => f.id === folderId);
			if (!folder) {
				if (folderId !== "my") {
					return false;
				}
			}

			const newsletter = (folder ? folder.newsletters : data.myNewsletters).find((n) => n.id === newsletterId);
			if (!newsletter) {
				return false;
			}

			if (folder?.selected) {
				return !(data.excludeSelectedNewslettersInFolder[folder?.id] || []).includes(newsletterId);
			}

			const isNewsletterIdInSelectedNewslettersOfFolder = (data.selectedNewslettersInFolder[folderId] || []).includes(newsletterId);
			return isNewsletterIdInSelectedNewslettersOfFolder || folder?.selected || false;
		},
		async startCopyNewsletters() {
			const { selectAll, folders, selectedNewslettersInFolder, excludeSelectedNewslettersInFolder, excludedFolderIds, myNewsletters } =
				get(store);
			const { totalNewsletters: totalNewslettersToCopy, totalFolders: totalFoldersToCopy } = calculateNumberOfNewslettersToCopy(get(store));
			const data = {
				select_all: selectAll,
				selected_folders_ids: folders.filter((f) => f.selected).map((f) => f.id),
				selected_newsletters_in_folder: selectedNewslettersInFolder,
				exclude_selected_newsletters_in_folder: excludeSelectedNewslettersInFolder,
				excluded_folder_ids: excludedFolderIds,
				total_newsletters: totalNewslettersToCopy,
				total_folders: totalFoldersToCopy
			};

			const copyRequestProgress: CopyRequestProgress = {
				id: "pending",
				copiedNewsletters: 0,
				errorCode: undefined,
				status: "pending",
				totalNewsletters: totalNewslettersToCopy,
				totalFolders: totalFoldersToCopy
			};

			try {
				store.update((values) => {
					return {
						...values,
						errorsDuringCopy: false,
						copyRequestProgress: copyRequestProgress
					};
				});
				const copyRequestId: string = await postJson<any>(`${classicWebUrl}/app/connect_copy/copy_newsletters`, {
					...data
				});
				openCopyRequestId = copyRequestId;
				store.update((values) => {
					return {
						...values,
						copyRequestId,
						copyRequestProgress: undefined
					};
				});
				refreshCheckProgress();
				return copyRequestId;
			} catch (e) {
				store.update((values) => {
					return {
						...values,
						errorsDuringCopy: true,
						copyRequestId: undefined,
						copyRequestProgress: undefined
					};
				});
				refreshCheckProgress();
				return (e as ServerError).code;
			}
		},
		resetStore() {
			store.update((values) => {
				values.folders.forEach((folder) => (folder.selected = false));
				return {
					...values,
					isLoading: false,
					selectAll: false,
					selectedNewslettersInFolder: {},
					excludeSelectedNewslettersInFolder: {},
					excludedFolderIds: [],
					errorsDuringCopy: false,
					copyRequestId: undefined,
					copyRequestProgress: undefined
				};
			});
		},
		updateCopyNewslettersProgress: updateCopyNewslettersProgressInternal,
		getCopyNewslettersProgress() {
			return derived(store, (values: StoreProps) => {
				return values.copyRequestProgress;
			});
		},
		async checkForExistingCopyRequest() {
			const existingCopyRequestResult = await getJson<{ id?: string; progress?: CopyRequestProgress }>(
				`${classicWebUrl}/app/connect_copy/existing_copy_request`
			);
			openCopyRequestId = existingCopyRequestResult?.id;
			if (existingCopyRequestResult.id) {
				store.update((values) => {
					return {
						...values,
						copyRequestId: existingCopyRequestResult?.id
					};
				});
				updateStoreWithCopyRequestProgress(existingCopyRequestResult?.progress);
			}
		},
		async markOperationAsSeen(id: string) {
			if (id !== "done") {
				await postJson(`${classicWebUrl}/app/connect_copy/mark_copy_request_as_notified_in_browser/${id}`);
			}
		}
	};
}

function calculateNumberOfNewslettersToCopy(values: StoreProps) {
	const { selectAll, folders, excludedFolderIds, excludeSelectedNewslettersInFolder, selectedNewslettersInFolder, myNewsletters } = values;
	let totalNewsletters = 0;
	let totalFolders = 0;
	if (selectAll) {
		totalNewsletters = folders.reduce((total, folder) => {
			if (!excludedFolderIds.includes(folder.id)) {
				const excludedNewslettersCount = excludeSelectedNewslettersInFolder[folder.id]?.length || 0;
				total += folder.count - excludedNewslettersCount;
			}
			return total;
		}, totalNewsletters);
		const excludedMyNewslettersCount = excludeSelectedNewslettersInFolder["my"]?.length || 0;
		totalNewsletters += myNewsletters.length - excludedMyNewslettersCount;
		totalFolders = folders.length - excludedFolderIds.length;
	} else {
		totalNewsletters = folders.reduce((total, folder) => {
			if (folder.selected) {
				totalFolders += 1;
				total += folder.count;
				const excludedNewslettersCount = excludeSelectedNewslettersInFolder[folder.id]?.length || 0;
				total -= excludedNewslettersCount;
			} else {
				const selectedNewslettersCount = selectedNewslettersInFolder[folder.id]?.length || 0;
				total += selectedNewslettersCount;
				if (selectedNewslettersCount > 0) {
					totalFolders += 1;
				}
			}
			return total;
		}, totalNewsletters);
		const selectedMyNewslettersCount = selectedNewslettersInFolder["my"]?.length || 0;
		totalNewsletters += selectedMyNewslettersCount;
	}

	return { totalNewsletters, totalFolders };
}
